import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import cn from 'classnames';
import './OpportunityListingExplanation.scss';

import { matchScrollHeight } from '../../../Helpers/helpers';
import { enhanceHTML } from '../../../Helpers/formatting';
import { getOpportunityWarningForField, opportunityFieldMaxLengths } from '../../../Helpers/opportunity_helpers';

const OpportunityListingExplanation = props => {
  const { opportunity, isEditing, description, guidelines, additionalPerks, updateField, hasLoadedFullOpportunity } = props;
  const { type } = opportunity || {};
  const isBonus = type === 'bonus';

  const formatBullets = text => {
    const lines = text?.split('\n') || [];
    return (
      <ul>
        {lines.map((line, index) => {
          const formattedLine = line.trim().startsWith('-') ? line.replace('- ', '') : line.trim().startsWith('•') ? line.replace('• ', '') : line;
          return formattedLine && <li key={index}>{enhanceHTML(formattedLine.trim())}</li>;
        })}
      </ul>
    );
  };

  const additionalClasses = { editing: isEditing };

  const showAboutSection = (isEditing || !!description) && !(isBonus && !description && window.__IS_NEW_OPPS_BONUS_MODE_LIVE__);
  const showGuidelinesSection = (isEditing || !!guidelines) && !(isBonus && !guidelines && window.__IS_NEW_OPPS_BONUS_MODE_LIVE__);
  const showPerksSection = isEditing || !!additionalPerks;

  // Warnings & Alerts
  const descriptionWarning = getOpportunityWarningForField(opportunity, 'description');
  const maxDescriptionLength = isBonus ? opportunityFieldMaxLengths.descriptionBonus : opportunityFieldMaxLengths.description;
  return (
    <>
      {showAboutSection && (
        <div className='opportunity-listing-section opportunity-listing-explanation-section'>
          <div className='opportunity-listing-section-title-container'>
            <div className='opportunity-listing-section-title'>About This Opportunity</div>
          </div>
          <div className={cn('opportunity-listing-content', additionalClasses)}>
            {isEditing ? (
              <div className='content-input-wrapper'>
                <textarea
                  rows={3}
                  className='content'
                  placeholder='Please explain this opportunity.'
                  value={description || ''}
                  onChange={e => updateField('description', e.target.value)}
                  ref={ref => matchScrollHeight(ref)}
                />
                <div
                  className={cn('char-count', {
                    warning: description?.length > maxDescriptionLength
                  })}
                >
                  {description?.length || 0} / {maxDescriptionLength}
                </div>
              </div>
            ) : hasLoadedFullOpportunity ? (
              description ? (
                <div className='content'>{enhanceHTML(description)}</div>
              ) : (
                <div className='content empty'>There is no description for this opportunity.</div>
              )
            ) : (
              <div className='loading-content'>Loading Description...</div>
            )}
            {isEditing && !!descriptionWarning && (
              <div className='warning-alert'>
                <div className='icon'>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                </div>
                <div className='text'>{descriptionWarning}</div>
              </div>
            )}
          </div>
        </div>
      )}
      {showGuidelinesSection && (
        <div className='opportunity-listing-section opportunity-listing-explanation-section'>
          <div className='opportunity-listing-section-title-container'>
            <div className='opportunity-listing-section-title'>Guidelines</div>
            {isEditing && (
              <div className='opportunity-listing-section-subtitle'>
                Please type individual guidelines on separate lines. Please note that guidelines are not requirements, they are guidance for creators.
                They will not be enforced in the automated check for completion. If you would like a more formal agreement, please use the
                collaborations feature.
              </div>
            )}
          </div>
          <div className={cn('opportunity-listing-content', additionalClasses)}>
            {isEditing ? (
              <div className='content-input-wrapper'>
                <textarea
                  rows={3}
                  className='content'
                  placeholder='What should creators know about what you are looking for?'
                  value={guidelines || ''}
                  onChange={e => updateField('guidelines', e.target.value)}
                  ref={ref => matchScrollHeight(ref)}
                />
              </div>
            ) : hasLoadedFullOpportunity ? (
              guidelines ? (
                <div className='content'>{formatBullets(guidelines)}</div>
              ) : (
                <div className='content empty'>There are no guidelines for this opportunity.</div>
              )
            ) : (
              <div className='loading-content'>Loading Guidelines...</div>
            )}
          </div>
        </div>
      )}
      {showPerksSection && (
        <div className='opportunity-listing-section opportunity-listing-explanation-section'>
          <div className='opportunity-listing-section-title-container'>
            <div className='opportunity-listing-section-title'>What Else You Get</div>
          </div>
          {(isEditing || additionalPerks) && (
            <div className={cn('opportunity-listing-content', additionalClasses)}>
              {isEditing ? (
                <div className='content-input-wrapper'>
                  <textarea
                    rows={3}
                    className='content'
                    placeholder='Are there any additional perks beyond the payment tiers below? This is optional.'
                    value={additionalPerks || ''}
                    onChange={e => updateField('additionalPerks', e.target.value)}
                    ref={ref => matchScrollHeight(ref)}
                  />
                </div>
              ) : (
                <div className='content'>{formatBullets(additionalPerks)}</div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

OpportunityListingExplanation.propTypes = {
  user: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  opportunity: PropTypes.object.isRequired,
  opportunityRequest: PropTypes.object,
  canEdit: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool.isRequired,
  description: PropTypes.string,
  guidelines: PropTypes.string,
  additionalPerks: PropTypes.string,
  updateField: PropTypes.func,
  hasLoadedFullOpportunity: PropTypes.bool
};

export default OpportunityListingExplanation;
